import { memo, useEffect, useRef, useState } from "react";
import { Avatar, Tooltip, Image, message as antMessage } from "antd";
import { useTranslation } from "react-i18next";
import { LoadingOutlined, ExclamationCircleOutlined, CopyOutlined } from "@ant-design/icons";
import { ExternalLink, ImageOff } from "lucide-react";

/**
 * Video trong tin nhắn. Link CDN của Meta có chữ ký nên sẽ hết hạn: khi hỏng,
 * trình duyệt thường treo ở trạng thái đang tải chứ KHÔNG bắn sự kiện error,
 * nên quá 8 giây chưa có metadata thì coi như hỏng và rơi về link mở tab mới.
 */
function VideoPlayer({ src }: { src: string }) {
    const { t } = useTranslation();
    const [failed, setFailed] = useState(false);
    const [ready, setReady] = useState(false);
    const videoRef = useRef<HTMLVideoElement>(null);

    useEffect(() => {
        if (ready || failed) return;
        const timer = setTimeout(() => {
            if ((videoRef.current?.readyState ?? 0) === 0) setFailed(true);
        }, 8000);
        return () => clearTimeout(timer);
    }, [ready, failed]);

    if (failed) {
        return (
            <div className="w-[260px] max-w-full">
                <div className="text-[12px] text-gray-500 dark:text-gray-400">
                    {t("roomchat.video_expired")}
                </div>
                <a
                    href={src}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className="inline-flex items-center gap-1 text-[11px] text-[#3e91ab] hover:underline mt-1"
                >
                    <ExternalLink size={11} />
                    {t("roomchat.video_open")}
                </a>
            </div>
        );
    }

    return (
        <video
            ref={videoRef}
            src={src}
            controls
            playsInline
            preload="metadata"
            onLoadedMetadata={() => setReady(true)}
            onError={() => setFailed(true)}
            className="w-[260px] max-w-full max-h-[320px] rounded-lg bg-black"
        />
    );
}

/**
 * Ảnh trong tin nhắn. Link ảnh CDN của Meta có chữ ký nên sẽ hết hạn: tải lỗi thì
 * hiện ô báo "ảnh không còn" thay vì khung ảnh vỡ của trình duyệt.
 */
function ChatImage({ src, onCopy }: { src: string; onCopy: (url: string) => void }) {
    const { t } = useTranslation();
    const [failed, setFailed] = useState(false);

    // Đổi ảnh (vd tin tạm được thay bằng tin thật từ server) thì thử tải lại
    useEffect(() => setFailed(false), [src]);

    if (failed) {
        return (
            <div
                className="w-[100px] h-[100px] rounded-lg border border-dashed border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-[#1f1f1f] flex flex-col items-center justify-center gap-1 px-2 text-center text-[11px] leading-tight text-gray-400"
                title={t("roomchat.image_expired")}
            >
                <ImageOff size={18} />
                {t("roomchat.image_expired")}
            </div>
        );
    }

    return (
        <div className="relative group">
            <Image
                src={src}
                width={100}
                height={100}
                className="rounded-lg object-cover"
                onError={() => setFailed(true)}
                preview={{
                    mask: t("roomchat.view_image")
                }}
            />

            <button
                type="button"
                aria-label={t("roomchat.copy_image")}
                onClick={(e) => {
                    e.stopPropagation();
                    onCopy(src);
                }}
                className="
                    absolute top-1 right-1
                    w-6 h-6
                    rounded
                    bg-black/60
                    text-white
                    opacity-0
                    group-hover:opacity-100
                    focus:opacity-100
                    transition
                    flex items-center justify-center
                "
            >
                <CopyOutlined />
            </button>
        </div>
    );
}

interface Props {
    text: string
    sender: string
    /** Tên nhân viên đã gửi tin (chỉ truyền ở tin đầu mỗi chuỗi tin cùng người gửi) */
    staffName?: string
    time: string
    isMe?: boolean
    avatar?: string
    images?: { preview: string; type: string }[]
    /** Tin nhắn thoại: danh sách URL file âm thanh (.ogg từ CDN của Meta) */
    audios?: string[]
    /** Tin nhắn video: danh sách URL file video từ CDN của kênh */
    videos?: string[]
    pending?: boolean
    failed?: boolean
    /** Tin gửi lỗi: gửi lại phần chưa đi được */
    onRetry?: () => void
    /** Tin gửi lỗi: bỏ tin khỏi khung chat (chưa gửi nên chỉ xoá ở máy) */
    onDelete?: () => void
}

/** Bong bóng tin chỉ render lại khi props đổi (khung chat render lại không kéo theo cả danh sách) */
export default memo(MessageBubble);

function MessageBubble({
    text,
    sender,
    staffName,
    time,
    isMe,
    avatar,
    images,
    audios,
    videos,
    pending,
    failed,
    onRetry,
    onDelete,
}: Props) {
    const { t } = useTranslation();

    const hasImages = images && images.length > 0;
    const hasAudios = audios && audios.length > 0;
    const hasVideos = videos && videos.length > 0;

    // Tin nhắn rỗng (không nội dung, không ảnh, không phải đang gửi/lỗi) -> ẩn, tránh bong bóng trống
    if (!text?.trim() && !hasImages && !hasAudios && !hasVideos && !pending && !failed) {
        return null;
    }

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(text);
            antMessage.success(t("roomchat.copied"));
        } catch {
            antMessage.error(t("roomchat.copy_failed"));
        }
    };

    // Chuyển blob ảnh bất kỳ -> PNG (trình duyệt chỉ ghi clipboard được image/png).
    const blobToPng = (blob: Blob): Promise<Blob> =>
        new Promise((resolve, reject) => {
            const img = new window.Image();
            const url = URL.createObjectURL(blob);
            img.onload = () => {
                const canvas = document.createElement("canvas");
                canvas.width = img.naturalWidth;
                canvas.height = img.naturalHeight;
                const ctx = canvas.getContext("2d");
                if (!ctx) { URL.revokeObjectURL(url); reject(new Error("no ctx")); return; }
                ctx.drawImage(img, 0, 0);
                URL.revokeObjectURL(url);
                canvas.toBlob((b) => (b ? resolve(b) : reject(new Error("toBlob failed"))), "image/png");
            };
            img.onerror = () => { URL.revokeObjectURL(url); reject(new Error("load failed")); };
            img.src = url;
        });

    const handleCopyImage = async (url: string) => {
        try {
            // Cần Clipboard API hỗ trợ ghi ảnh
            if (typeof ClipboardItem === "undefined" || !navigator.clipboard?.write) {
                throw new Error("ClipboardItem unsupported");
            }
            const res = await fetch(url, { mode: "cors" });
            const blob = await res.blob();
            const pngBlob = blob.type === "image/png" ? blob : await blobToPng(blob);
            await navigator.clipboard.write([new ClipboardItem({ "image/png": pngBlob })]);
            antMessage.success(t("roomchat.copied"));
            // Báo cho ô Ghi chú biết clipboard vừa có ảnh -> hiện badge "Sẵn sàng dán"
            window.dispatchEvent(new Event("clipboard-image-copied"));
        } catch {
            // Fallback: không copy được ảnh (CORS/không hỗ trợ) -> copy link ảnh
            try {
                await navigator.clipboard.writeText(url);
                antMessage.success(t("roomchat.copied"));
            } catch {
                antMessage.error(t("roomchat.copy_failed"));
            }
        }
    };

    const tooltipContent = (
        <div className="text-xs">
            {/* <div className="font-semibold">{sender}</div>
            <div className="text-gray-400">{time}</div>
            {text && <div className="mt-1">{text}</div>} */}
            <div>
                {text && (
                    <button
                        type="button"
                        onClick={handleCopy}
                        className="flex items-center gap-1 text-[#9fd2e3] hover:text-white transition cursor-pointer h-[20px]"
                    >
                        <CopyOutlined />
                        {/* {t("roomchat.copy_message")} */}
                    </button>
                )}
                {/* {hasImages && (
                    <button
                        type="button"
                        onClick={() => handleCopyImage(images![0].preview)}
                        className="flex items-center gap-1 text-[#9fd2e3] hover:text-white transition cursor-pointer"
                    >
                        <CopyOutlined /> {t("roomchat.copy_image")}
                    </button>
                )} */}
            </div>
        </div>
    );

    return (
        <div className={`flex mb-2 ${isMe ? "justify-end" : "justify-start"}`}>

            {/* avatar left */}
            {!isMe && (
                <Avatar
                    src={avatar || undefined}
                    className="w-8 h-8 flex-shrink-0 mr-2 mt-[2px] bg-[#3e91ab] text-white"
                    style={{ minWidth: 32 }}
                >
                    {sender?.charAt(0)?.toUpperCase() || "?"}
                </Avatar>
            )}

            <div className="flex flex-col items-end max-w-[420px]">

                {isMe && staffName && (
                    <div className="text-[11px] text-gray-500 dark:text-gray-400 mb-[2px] mr-1 max-w-full truncate">
                        {staffName}
                    </div>
                )}

                {/* Chỉ render bong bóng text khi có nội dung -> tránh bong bóng trống ở tin nhắn ảnh */}
                {!!text?.trim() && (
                    <Tooltip
                        title={tooltipContent}
                        placement={isMe ? "left" : "right"}
                        mouseLeaveDelay={0.3}
                    >
                        <div
                            className={`
                                px-3 py-2
                                !rounded-[8px]
                                shadow-sm
                                cursor-default
                                !text-[14px]
                                text-000000e0
                                transition-opacity duration-150
                                msg-text bg-white p-2.5 rounded-r-lg rounded-bl-lg shadow-sm border border-gray-100 text-xs text-gray-800
                                mb-1
                                ${pending ? 'opacity-60' : 'opacity-100'}
                                ${isMe
                                    ? "bg-[#e1f0f5] dark:bg-[#262626]"
                                    : "bg-white dark:bg-[#262626]"
                                }
                            `}
                        >
                            <div>{text}</div>
                        </div>
                    </Tooltip>
                )}


                {hasVideos && (
                    <div
                        className={`
                            p-1
                            !rounded-[8px]
                            shadow-sm
                            cursor-default
                            text-000000e0
                            transition-opacity duration-150
                            msg-text rounded-r-lg rounded-bl-lg border border-gray-100
                            mb-1
                            ${pending ? 'opacity-60' : 'opacity-100'}
                            ${isMe ? "bg-[#e1f0f5] dark:bg-[#262626]" : "bg-white dark:bg-[#262626]"}
                        `}
                    >
                        <div className="flex flex-col gap-2">
                            {videos!.map((src, i) => (
                                <VideoPlayer key={i} src={src} />
                            ))}
                        </div>
                    </div>
                )}

                {hasAudios && (
                    <div
                        className={`
                            px-3 py-2
                            !rounded-[8px]
                            shadow-sm
                            cursor-default
                            text-000000e0
                            transition-opacity duration-150
                            msg-text rounded-r-lg rounded-bl-lg border border-gray-100
                            mb-1
                            ${pending ? 'opacity-60' : 'opacity-100'}
                            ${isMe ? "bg-[#e1f0f5] dark:bg-[#262626]" : "bg-white dark:bg-[#262626]"}
                        `}
                    >
                        <div className="flex flex-col gap-2">
                            {audios!.map((src, i) => (
                                <audio
                                    key={i}
                                    src={src}
                                    controls
                                    preload="metadata"
                                    className="w-[240px] max-w-full"
                                />
                            ))}
                        </div>
                    </div>
                )}

                {hasImages && (
                    <div
                        className={`
                        ${hasImages && !text ? 'p-1' : 'px-3 py-2'}
                        !rounded-[8px]
                        shadow-sm
                        cursor-default
                        !text-[14px]
                        text-000000e0
                        transition-opacity duration-150
                        msg-text bg-white p-2.5 rounded-r-lg rounded-bl-lg shadow-sm border border-gray-100 text-xs text-gray-800
                        ${pending ? 'opacity-60' : 'opacity-100'}
                        ${isMe
                                ? "bg-[#e1f0f5] dark:bg-[#262626]"
                                : "bg-white dark:bg-[#262626]"
                            }
                    `}
                    >
                        <div className={`flex gap-2 flex-wrap ${text ? 'mt-1' : ''}`}>
                            {images.map((img, i) => (
                                <ChatImage key={i} src={img.preview} onCopy={handleCopyImage} />
                            ))}
                        </div>
                    </div>
                )}

                {/* Pending / Failed status indicator */}
                {isMe && (
                    <div className="mt-[2px] mr-1 text-[11px] flex items-center gap-1">
                        {pending && (
                            <span className="text-gray-400 flex items-center gap-1">
                                <LoadingOutlined style={{ fontSize: 11 }} spin />
                                {t("roomchat.sending") || "Đang gửi..."}
                            </span>
                        )}
                        {failed && (
                            <span className="text-red-500 flex items-center gap-1">
                                <ExclamationCircleOutlined style={{ fontSize: 11 }} />
                                {t("roomchat.send_failed") || "Gửi thất bại"}
                                {onRetry && (
                                    <button type="button" onClick={onRetry} className="ml-1 font-medium text-[#3e91ab] hover:underline">
                                        {t("roomchat.retry_send")}
                                    </button>
                                )}
                                {onDelete && (
                                    <button type="button" onClick={onDelete} className="ml-1 text-gray-500 hover:text-red-500 hover:underline">
                                        {t("roomchat.delete_failed_message")}
                                    </button>
                                )}
                            </span>
                        )}
                    </div>
                )}
            </div>

            {/* avatar right - ẩn avatar tin nhắn từ web (sẽ mở lại sau)
            {isMe && (
                <Avatar
                    src={avatar || undefined}
                    className="w-8 h-8 flex-shrink-0 ml-2 mt-[2px] bg-[#3e91ab] text-[#ffffff] dark:bg-[#2f6f83]"
                    style={{ minWidth: 32 }}
                >
                    {sender?.charAt(0)?.toUpperCase() || "?"}
                </Avatar>
            )}
            */}

        </div>
    );
}